/**
 * Vintage Ribelle - Core Theme JavaScript
 * Premium vintage fashion brand
 * Vanilla JS, ES6+, zero dependencies
 *
 * Handles: header scroll behavior, mobile menu, search toggle,
 *          quantity inputs, back-to-top, cart count updates,
 *          section load handling, responsive helpers,
 *          lazy loading, and shared utilities.
 */

class VintageRibelle {
  constructor() {
    this.header = document.querySelector('.header');
    this.mobileMenu = document.querySelector('.mobile-menu');
    this.mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle], [data-menu-toggle]');
    this.mobileMenuCloseBtn = document.querySelector('[data-mobile-menu-close], [data-menu-close]');
    this.searchToggle = document.querySelector('[data-search-toggle]');
    this.searchBar = document.querySelector('[data-search-bar]');
    this.searchInput = document.querySelector('[data-search-input]');
    this.searchCloseBtn = document.querySelector('[data-search-close]');
    this.backToTopBtn = document.querySelector('[data-back-to-top]');
    this.cartCountEl = document.querySelector('[data-cart-count]');
    this.cartDrawer = document.querySelector('.cart-drawer');

    this.isHomePage = document.body.classList.contains('template-index');
    this.lastScrollY = 0;
    this.scrollTicking = false;
    this.scrollDirection = 'none';
    this.scrollAccumulator = 0;
    this.headerHidden = false;

    this.init();
  }

  /* ==================================================================
     Initialization
     ================================================================== */

  init() {
    this.setInitialHeaderState();
    this.initHeader();
    this.initMobileMenu();
    this.initSearchToggle();
    this.initQuantityInputs();
    this.initBackToTop();
    this.initCartCountUpdate();
    this.initSectionLoadHandling();
    this.initLazyLoadImages();
    this.bindEvents();
  }

  /* ==================================================================
     1. Header Behavior — ENHANCED
     ================================================================== */

  setInitialHeaderState() {
    if (!this.header) return;

    if (this.isHomePage) {
      this.header.classList.add('header--transparent');
    }
  }

  initHeader() {
    if (!this.header) return;
    window.addEventListener('scroll', () => this.onHeaderScroll(), { passive: true });
  }

  onHeaderScroll() {
    if (this.scrollTicking) return;
    this.scrollTicking = true;

    requestAnimationFrame(() => {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const scrollingDown = scrollY > this.lastScrollY;
      const scrollDelta = scrollY - this.lastScrollY;

      // Track scroll direction and accumulate delta
      if (scrollingDown) {
        this.scrollAccumulator = Math.min(
          200,
          this.scrollAccumulator + Math.abs(scrollDelta)
        );
      } else {
        this.scrollAccumulator = Math.max(
          -200,
          this.scrollAccumulator - Math.abs(scrollDelta)
        );
      }

      // Determine scrolled state (hero threshold)
      if (this.isHomePage) {
        const hero = document.querySelector('.hero-ribelle');
        const heroThreshold = hero
          ? Math.min(hero.offsetHeight * 0.6, 150)
          : 150;
        if (scrollY > heroThreshold) {
          this.header.classList.add('is-scrolled');
        } else {
          this.header.classList.remove('is-scrolled');
        }
      }

      // Hide/show header based on scroll direction
      // Must scroll at least 80px accumulated before hiding
      const HIDE_THRESHOLD = 80;

      if (this.scrollAccumulator > HIDE_THRESHOLD && scrollY > 200) {
        if (!this.headerHidden) {
          this.header.classList.add('is-hidden');
          this.headerHidden = true;
        }
      } else if (this.scrollAccumulator < -HIDE_THRESHOLD || scrollY <= 100) {
        if (this.headerHidden) {
          this.header.classList.remove('is-hidden');
          this.headerHidden = false;
        }
      }

      // Clamp accumulator
      this.scrollAccumulator = Math.max(-200, Math.min(200, this.scrollAccumulator));

      this.lastScrollY = scrollY;
      this.scrollTicking = false;
    });
  }

  /* ==================================================================
     2. Mobile Menu — ENHANCED
     ================================================================== */

  initMobileMenu() {
    if (!this.mobileMenu) return;

    // Toggle button
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMobileMenu();
      });
    }

    // Close triggers
    const closeTriggers = this.mobileMenu.querySelectorAll('[data-mobile-menu-close], [data-menu-close]');
    closeTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeMobileMenu();
      });
    });

    // Overlay click
    const overlay = this.mobileMenu.querySelector('.mobile-menu__backdrop, .mobile-menu__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.closeMobileMenu());
    }

    const submenuToggles = this.mobileMenu.querySelectorAll('[data-submenu-toggle]');
    submenuToggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const submenu = toggle.parentElement.querySelector('[data-submenu]');
        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isOpen));
        if (submenu) {
          submenu.setAttribute('aria-hidden', String(isOpen));
          submenu.classList.toggle('is-open', !isOpen);
        }
      });
    });

    // Nav link click closes menu
    const navLinks = this.mobileMenu.querySelectorAll(
      '.mobile-menu__nav a[href]'
    );
    navLinks.forEach((link) => {
      link.addEventListener('click', () => {
        setTimeout(() => this.closeMobileMenu(), 150);
      });
    });
  }

  openMobileMenu() {
    if (!this.mobileMenu) return;

    this.mobileMenu.classList.add('is-open');

    // Animate hamburger to X
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.classList.add('is-active');
      this.mobileMenuToggle.setAttribute('aria-expanded', 'true');
    }

    this.mobileMenu.setAttribute('aria-hidden', 'false');

    // Prevent body scroll with position:fixed trick (prevents layout shift)
    const scrollY = window.pageYOffset;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.dataset.scrollY = scrollY;

    this.animateMobileMenuLinks();
    this.trapFocusInMobileMenu();

    document.dispatchEvent(new CustomEvent('menu:opened'));
  }

  closeMobileMenu() {
    if (!this.mobileMenu) return;

    this.mobileMenu.classList.remove('is-open');

    // Animate hamburger back
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.classList.remove('is-active');
      this.mobileMenuToggle.setAttribute('aria-expanded', 'false');
    }

    this.mobileMenu.setAttribute('aria-hidden', 'true');

    // Restore body scroll
    const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    delete document.body.dataset.scrollY;
    window.scrollTo(0, scrollY);

    this.resetMobileMenuLinks();

    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.focus();
    }

    document.dispatchEvent(new CustomEvent('menu:closed'));
  }

  toggleMobileMenu() {
    if (!this.mobileMenu) return;
    if (this.mobileMenu.classList.contains('is-open')) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  animateMobileMenuLinks() {
    const links = this.mobileMenu.querySelectorAll(
      '.mobile-menu__nav a, .mobile-menu__nav button'
    );
    links.forEach((link, index) => {
      link.style.transitionDelay = `${index * 0.05}s`;
      link.style.opacity = '0';
      // Force reflow
      link.getBoundingClientRect();
      link.style.opacity = '1';
    });
  }

  resetMobileMenuLinks() {
    const links = this.mobileMenu.querySelectorAll(
      '.mobile-menu__nav a, .mobile-menu__nav button'
    );
    links.forEach((link) => {
      link.style.transitionDelay = '0s';
      link.style.opacity = '';
    });
  }

  trapFocusInMobileMenu() {
    if (!this.mobileMenu) return;

    const focusableSelector =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([type="hidden"]), select, textarea, [contenteditable]';
    const focusableElements = this.mobileMenu.querySelectorAll(focusableSelector);

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    setTimeout(() => {
      if (firstFocusable) firstFocusable.focus();
    }, 100);

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    this.mobileMenu.addEventListener('keydown', handleTab);

    const cleanup = () => {
      this.mobileMenu.removeEventListener('keydown', handleTab);
      document.removeEventListener('menu:closed', cleanup);
    };
    document.addEventListener('menu:closed', cleanup, { once: true });
  }

  /* ==================================================================
     3. Search Toggle — ENHANCED
     ================================================================== */

  initSearchToggle() {
    if (!this.searchToggle) return;

    this.searchToggle.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleSearch();
    });

    if (this.searchCloseBtn) {
      this.searchCloseBtn.addEventListener('click', () => this.closeSearch());
    }

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!this.searchBar || !this.searchBar.classList.contains('is-open')) return;
      if (
        this.searchBar.contains(e.target) ||
        (this.searchToggle && this.searchToggle.contains(e.target))
      ) {
        return;
      }
      this.closeSearch();
    });
  }

  toggleSearch() {
    if (this.searchBar && this.searchBar.classList.contains('is-open')) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    if (!this.searchBar) return;
    this.searchBar.classList.add('is-open');

    if (this.searchInput) {
      setTimeout(() => {
        this.searchInput.focus();
      }, 150);
    }

    document.dispatchEvent(new CustomEvent('search:opened'));
  }

  closeSearch() {
    if (!this.searchBar) return;
    this.searchBar.classList.remove('is-open');

    if (this.searchInput) {
      this.searchInput.value = '';
    }

    if (this.searchToggle) {
      this.searchToggle.focus();
    }

    document.dispatchEvent(new CustomEvent('search:closed'));
  }

  /* ==================================================================
     4. Back to Top — ENHANCED
     ================================================================== */

  initBackToTop() {
    if (!this.backToTopBtn) return;

    window.addEventListener(
      'scroll',
      this.throttle(() => {
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;
        if (scrollY > 600) {
          this.backToTopBtn.classList.add('is-visible');
        } else {
          this.backToTopBtn.classList.remove('is-visible');
        }
      }, 150),
      { passive: true }
    );

    this.backToTopBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.scrollToTop();
    });
  }

  scrollToTop() {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      window.scrollTo(0, 0);
      return;
    }

    // Smooth manual animation
    const startPosition = window.pageYOffset;
    const duration = 600;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const position = startPosition * (1 - eased);

      window.scrollTo(0, position);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  /* ==================================================================
     5. Quantity Inputs
     ================================================================== */

  initQuantityInputs() {
    const wrappers = document.querySelectorAll('.quantity-input');

    wrappers.forEach((wrapper) => {
      const btnMinus = wrapper.querySelector('.quantity-input__btn--minus');
      const btnPlus = wrapper.querySelector('.quantity-input__btn--plus');
      const field = wrapper.querySelector('.quantity-input__field');

      if (!field) return;

      if (btnMinus) {
        btnMinus.addEventListener('click', (e) => {
          e.preventDefault();
          this.adjustQuantity(field, -1);
        });
      }

      if (btnPlus) {
        btnPlus.addEventListener('click', (e) => {
          e.preventDefault();
          this.adjustQuantity(field, 1);
        });
      }

      field.addEventListener('input', () => {
        this.sanitizeQuantityInput(field);
      });

      field.addEventListener('blur', () => {
        this.sanitizeQuantityInput(field);
      });

      // Also observe dynamically added quantity inputs
      // from Shopify section rendering
    });

    // MutationObserver for dynamically added quantity wrappers
    this._quantityObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          if (node.matches && node.matches('.quantity-input')) {
            this.initQuantityInputForWrapper(node);
          }

          if (node.querySelectorAll) {
            const nested = node.querySelectorAll('.quantity-input');
            nested.forEach((w) => this.initQuantityInputForWrapper(w));
          }
        });
      });
    });

    this._quantityObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  initQuantityInputForWrapper(wrapper) {
    if (wrapper._quantityBound) return;
    wrapper._quantityBound = true;

    const btnMinus = wrapper.querySelector('.quantity-input__btn--minus');
    const btnPlus = wrapper.querySelector('.quantity-input__btn--plus');
    const field = wrapper.querySelector('.quantity-input__field');

    if (!field) return;

    if (btnMinus) {
      btnMinus.addEventListener('click', (e) => {
        e.preventDefault();
        this.adjustQuantity(field, -1);
      });
    }

    if (btnPlus) {
      btnPlus.addEventListener('click', (e) => {
        e.preventDefault();
        this.adjustQuantity(field, 1);
      });
    }

    field.addEventListener('input', () => this.sanitizeQuantityInput(field));
    field.addEventListener('blur', () => this.sanitizeQuantityInput(field));
  }

  adjustQuantity(field, delta) {
    let value = parseInt(field.value, 10);
    if (isNaN(value)) value = 1;
    value = Math.max(1, value + delta);
    field.value = value;
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  sanitizeQuantityInput(field) {
    let value = parseInt(field.value, 10);
    if (isNaN(value) || value < 1) {
      field.value = 1;
    }
  }

  /* ==================================================================
     6. Header Cart Count Update — NEW
     ================================================================== */

  initCartCountUpdate() {
    // Listen for custom cart:updated event
    document.addEventListener('cart:updated', () => {
      this.updateCartCount();
    });

    // Listen for Shopify cart update XHR completions
    document.addEventListener('cart:updated', () => {
      this.updateCartCount();
    });
  }

  updateCartCount() {
    if (!this.cartCountEl) return;

    fetch('/cart.js')
      .then((response) => {
        if (!response.ok) throw new Error('Cart fetch failed');
        return response.json();
      })
      .then((cart) => {
        const count = cart.item_count || 0;
        const prevCount = parseInt(this.cartCountEl.textContent || '0', 10);

        this.cartCountEl.textContent = count;
        this.cartCountEl.setAttribute('data-count', String(count));

        // Animate count change if value differs
        if (count !== prevCount) {
          this.animateCartCount();
        }
      })
      .catch((err) => {
        console.warn('Vintage Ribelle: Could not update cart count', err);
      });
  }

  animateCartCount() {
    if (!this.cartCountEl) return;

    this.cartCountEl.style.transition = 'none';
    this.cartCountEl.style.transform = 'scale(1.3)';
    this.cartCountEl.style.color = '#c9a96e';

    requestAnimationFrame(() => {
      this.cartCountEl.style.transition =
        'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), color 0.3s ease';
      this.cartCountEl.style.transform = 'scale(1)';
      this.cartCountEl.style.color = '';
    });

    // Cleanup inline styles after animation
    setTimeout(() => {
      this.cartCountEl.style.transform = '';
      this.cartCountEl.style.transition = '';
    }, 350);
  }

  /* ==================================================================
     7. Section Load Handling — NEW
     ================================================================== */

  initSectionLoadHandling() {
    // Shopify sections rendered via AJAX dispatch 'shopify:section:load' events
    document.addEventListener('shopify:section:load', (e) => {
      const sectionId = e.detail ? e.detail.sectionId : null;
      if (!sectionId) return;

      // Re-initialize quantity inputs in the new section
      const sectionEl = document.getElementById(`shopify-section-${sectionId}`);
      if (sectionEl) {
        const wrappers = sectionEl.querySelectorAll('.quantity-input');
        wrappers.forEach((w) => {
          w._quantityBound = false;
          this.initQuantityInputForWrapper(w);
        });

        // Notify animation controller to re-scan
        document.dispatchEvent(new CustomEvent('section:loaded'));
      }
    });

    // Shopify section reorder
    document.addEventListener('shopify:section:reorder', () => {
      document.dispatchEvent(new CustomEvent('section:loaded'));
    });

    // Shopify block select/deselect
    document.addEventListener('shopify:block:select', () => {
      document.dispatchEvent(new CustomEvent('section:loaded'));
    });

    document.addEventListener('shopify:block:deselect', () => {
      // No action needed
    });
  }

  /* ==================================================================
     8. Responsive Helpers — ENHANCED
     ================================================================== */

  /**
   * Check if viewport is mobile width.
   */
  isMobile(breakpoint = 768) {
    return window.innerWidth < breakpoint;
  }

  /**
   * Check if viewport is tablet width.
   */
  isTablet(breakpointMin = 768, breakpointMax = 1024) {
    return (
      window.innerWidth >= breakpointMin && window.innerWidth < breakpointMax
    );
  }

  /**
   * Check if viewport is desktop width.
   */
  isDesktop(breakpoint = 1024) {
    return window.innerWidth >= breakpoint;
  }

  /* ==================================================================
     9. Cookie / Storage Helpers
     ================================================================== */

  /**
   * Get a cookie value by name.
   */
  getCookie(name) {
    const match = document.cookie.match(
      new RegExp(`(^| )${name}=([^;]+)`)
    );
    return match ? decodeURIComponent(match[2]) : null;
  }

  /**
   * Set a cookie.
   */
  setCookie(name, value, days = 7) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  /* ==================================================================
     10. Lazy Load Images — ENHANCED
     ================================================================== */

  initLazyLoadImages() {
    // Native lazy loading
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');
    lazyImages.forEach((img) => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
        }
        delete img.dataset.src;
      }
    });

    // Fallback: IntersectionObserver for data-src images
    if (!('loading' in HTMLImageElement.prototype)) {
      this.lazyLoadFallback();
    }

    // Also handle <source> elements in <picture>
    const pictures = document.querySelectorAll('picture');
    pictures.forEach((picture) => {
      const sources = picture.querySelectorAll('source[data-srcset]');
      sources.forEach((source) => {
        source.srcset = source.dataset.srcset;
        delete source.dataset.srcset;
      });

      const img = picture.querySelector('img[data-src]');
      if (img) {
        img.src = img.dataset.src;
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
        }
        delete img.dataset.src;
      }
    });
  }

  lazyLoadFallback() {
    if (this._lazyLoadObserver) {
      this._lazyLoadObserver.disconnect();
    }

    this._lazyLoadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
            }
            delete img.dataset.src;
          }

          this._lazyLoadObserver.unobserve(img);
        });
      },
      {
        rootMargin: '200px 0px 200px 0px',
        threshold: 0.01,
      }
    );

    const lazyImages = document.querySelectorAll('img[data-src]');
    lazyImages.forEach((img) => this._lazyLoadObserver.observe(img));
  }

  /* ==================================================================
     Global Event Bindings
     ================================================================== */

  bindEvents() {
    // Escape key handler
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;

      // Close mobile menu
      if (
        this.mobileMenu &&
        this.mobileMenu.classList.contains('is-open')
      ) {
        this.closeMobileMenu();
        return;
      }

      // Close search
      if (
        this.searchBar &&
        this.searchBar.classList.contains('is-open')
      ) {
        this.closeSearch();
        return;
      }

      // Close cart drawer
      if (
        this.cartDrawer &&
        this.cartDrawer.classList.contains('is-open')
      ) {
        this.cartDrawer.classList.remove('is-open');
        document.dispatchEvent(new CustomEvent('cart:closed'));
        return;
      }
    });

    // Resize handler: close mobile menu on desktop
    window.addEventListener(
      'resize',
      this.debounce(() => {
        if (
          !this.isMobile() &&
          this.mobileMenu &&
          this.mobileMenu.classList.contains('is-open')
        ) {
          this.closeMobileMenu();
        }
      }, 250)
    );

    // Reduced motion preference
    const motionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
    if (motionQuery.addEventListener) {
      motionQuery.addEventListener('change', (e) => {
        document.documentElement.classList.toggle(
          'reduced-motion',
          e.matches
        );
      });
    }

    if (motionQuery.matches) {
      document.documentElement.classList.add('reduced-motion');
    }

    // Cart drawer toggle (shared with theme)
    const cartToggle = document.querySelector('[data-cart-toggle]');
    if (cartToggle && this.cartDrawer) {
      cartToggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.cartDrawer.classList.toggle('is-open');
      });

      const cartClose = this.cartDrawer.querySelector(
        '[data-cart-close], .cart-drawer__close'
      );
      if (cartClose) {
        cartClose.addEventListener('click', () => {
          this.cartDrawer.classList.remove('is-open');
          document.dispatchEvent(new CustomEvent('cart:closed'));
        });
      }

      const cartOverlay = this.cartDrawer.querySelector(
        '.cart-drawer__overlay'
      );
      if (cartOverlay) {
        cartOverlay.addEventListener('click', () => {
          this.cartDrawer.classList.remove('is-open');
          document.dispatchEvent(new CustomEvent('cart:closed'));
        });
      }
    }
  }

  /* ==================================================================
     Utility Functions
     ================================================================== */

  /**
   * Throttle: limit function execution to once per `delay` ms.
   */
  throttle(fn, delay = 100) {
    let lastTime = 0;
    let timeoutId = null;

    return function (...args) {
      const now = Date.now();
      const remaining = delay - (now - lastTime);

      if (remaining <= 0) {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        lastTime = now;
        fn.apply(this, args);
      } else if (!timeoutId) {
        timeoutId = setTimeout(() => {
          lastTime = Date.now();
          timeoutId = null;
          fn.apply(this, args);
        }, remaining);
      }
    };
  }

  /**
   * Debounce: delay function execution until `delay` ms of inactivity.
   */
  debounce(fn, delay = 250) {
    let timeoutId = null;

    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        fn.apply(this, args);
        timeoutId = null;
      }, delay);
    };
  }
}

/* ==================================================================
   Bootstrap
   ================================================================== */

document.addEventListener('DOMContentLoaded', () => {
  window.vintageRibelle = new VintageRibelle();
});
